import { Listbox, ListboxButton, ListboxOption, ListboxOptions } from '@headlessui/react';
import { CheckIcon, ChevronUpDownIcon } from '@heroicons/react/20/solid';
import clsx from 'clsx';

export interface SelectOption<T extends string> {
    value: T;
    label: string;
    disabled?: boolean;
}

export interface SelectProps<T extends string> {
    value: T | null;
    onChange: (value: T) => void;
    options: SelectOption<T>[];
    placeholder?: string;
    id?: string;
    disabled?: boolean;
    invalid?: boolean;
    'aria-describedby'?: string;
    'aria-label'?: string;
    className?: string;
}

/** Headless UI listbox that looks like `.field`. */
export function Select<T extends string>({
    value,
    onChange,
    options,
    placeholder,
    invalid,
    className,
    ...rest
}: SelectProps<T>) {
    const current = options.find((o) => o.value === value);
    return (
        <Listbox value={value} onChange={onChange} disabled={rest.disabled}>
            <div className={clsx('relative', className)}>
                <ListboxButton
                    id={rest.id}
                    aria-invalid={invalid || undefined}
                    aria-describedby={rest['aria-describedby']}
                    aria-label={rest['aria-label'] && current ? `${rest['aria-label']}: ${current.label}` : rest['aria-label']}
                    className="field flex items-center justify-between gap-2 text-left"
                >
                    <span className={clsx('truncate', !current && 'text-subtle')}>{current?.label ?? placeholder ?? '\u00a0'}</span>
                    <ChevronUpDownIcon className="size-5 shrink-0 text-muted" aria-hidden="true" />
                </ListboxButton>
                <ListboxOptions
                    anchor="bottom start"
                    transition
                    className="z-50 max-h-72 w-[var(--button-width)] overflow-auto rounded-control bg-raised p-1 shadow-e2 outline-none transition duration-hover ease-snap [--anchor-gap:4px] data-[closed]:opacity-0"
                >
                    {options.map((o) => (
                        <ListboxOption
                            key={o.value}
                            value={o.value}
                            disabled={o.disabled}
                            className="group flex min-h-10 cursor-pointer items-center justify-between gap-2 rounded-lg px-3 text-small text-strong data-[focus]:bg-hover data-[disabled]:opacity-50 [@media(pointer:coarse)]:min-h-11"
                        >
                            <span className="group-data-[selected]:font-semibold">{o.label}</span>
                            <CheckIcon className="invisible size-5 group-data-[selected]:visible" aria-hidden="true" />
                        </ListboxOption>
                    ))}
                </ListboxOptions>
            </div>
        </Listbox>
    );
}
